import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { setActiveRegistration } from "../lib/api";
import { Input } from "../components/ui/input";
import { Buildings, CaretRight, MagnifyingGlass, SignOut } from "@phosphor-icons/react";

const clearPickFlag = () => { try { sessionStorage.removeItem("claro-entity-unpicked"); } catch { /* noop */ } };

// Post-login "Select your entity" screen for users assigned to multiple
// registered entities. Users with a single entity (or super-admins) skip
// straight to the workspace.
export default function EntityPicker() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [regs, setRegs] = useState(null); // null = loading
  const [filter, setFilter] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.get("/registrations").then((r) => {
      if (cancelled) return;
      const list = (r.data?.registrations || []).filter((x) => x.member !== false);
      // Vanity/path-style login (app.clarocompliance.net/pec): if we captured an
      // org slug at sign-in, auto-select that entity when the user is a member.
      let org = null;
      try { org = sessionStorage.getItem("claro-login-org"); sessionStorage.removeItem("claro-login-org"); } catch { /* noop */ }
      if (org) {
        const match = list.find((x) => (x.slug || "").toLowerCase() === org.toLowerCase());
        if (match) {
          setActiveRegistration(match.id);
          api.post("/session/lock-tenant", { reg_id: match.id }).catch(() => {});
          clearPickFlag();
          nav("/", { replace: true });
          return;
        }
      }
      // Super-admins and single-entity users don't need to choose.
      if (user?.is_superadmin || list.length <= 1) {
        if (list.length === 1) {
          setActiveRegistration(list[0].id);
          api.post("/session/lock-tenant", { reg_id: list[0].id }).catch(() => {});
        }
        clearPickFlag();
        nav("/", { replace: true });
        return;
      }
      setRegs(list);
    }).catch(() => {
      clearPickFlag();
      nav("/", { replace: true });
    });
    return () => { cancelled = true; };
  }, [user, nav]);

  const pick = (id) => {
    setActiveRegistration(id);
    api.post("/session/lock-tenant", { reg_id: id }).catch(() => {});
    clearPickFlag();
    nav("/", { replace: true });
  };

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q || !regs) return regs || [];
    return regs.filter((r) =>
      (r.name || "").toLowerCase().includes(q) || (r.nerc_id || "").toLowerCase().includes(q));
  }, [regs, filter]);

  if (regs === null) {
    return (
      <div className="h-screen flex items-center justify-center font-mono text-sm text-muted-foreground">
        Loading your entities…
      </div>
    );
  }

  return (
    <div data-testid="entity-picker" className="font-inter min-h-screen flex flex-col bg-background">
      {/* Brand header */}
      <div className="bg-[#071B3E] text-white px-6 sm:px-10 py-6">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/claro-shield.png?v=3" alt="ClaroShield" className="h-10 w-auto object-contain" />
            <div>
              <div className="font-extrabold text-lg tracking-tight leading-none">ClaroShield</div>
              <div className="overline text-white/50 mt-1">Compliance</div>
            </div>
          </div>
          <button
            type="button"
            data-testid="entity-picker-signout"
            onClick={() => { logout(); nav("/login"); }}
            className="flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors">
            <SignOut size={16} /> Sign out
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 px-6 sm:px-10 py-10">
        <div className="max-w-5xl mx-auto">
          <div className="overline text-muted-foreground mb-2">Welcome back{user?.name ? `, ${user.name}` : ""}</div>
          <h1 className="font-extrabold text-3xl sm:text-4xl tracking-tight mb-2">Select an entity</h1>
          <p className="text-sm text-muted-foreground mb-8 max-w-2xl">
            Your account has access to multiple registered entities. Choose the one you want to work in.
            You can switch entities anytime from the sidebar.
          </p>

          {regs.length > 6 && (
            <div className="relative mb-6 max-w-md">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="entity-picker-search"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Search by name or NERC ID…"
                className="rounded-none pl-9"
              />
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((r) => (
              <button
                key={r.id}
                type="button"
                data-testid={`entity-card-${r.id}`}
                onClick={() => pick(r.id)}
                className="group text-left border border-border bg-card hover:border-[#071B3E] hover:shadow-md transition-all p-5 flex items-start gap-3">
                <div className="mt-0.5 shrink-0 h-10 w-10 flex items-center justify-center bg-[#071B3E]/5 text-[#071B3E] group-hover:bg-[#071B3E] group-hover:text-white transition-colors">
                  <Buildings size={20} weight="fill" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-base truncate group-hover:text-[#071B3E]">{r.name || "Unnamed entity"}</div>
                  {r.nerc_id && <div className="text-xs text-muted-foreground mt-0.5 truncate">NERC ID: {r.nerc_id}</div>}
                </div>
                <CaretRight size={18} className="mt-1 shrink-0 text-muted-foreground group-hover:text-[#071B3E] group-hover:translate-x-0.5 transition-all" />
              </button>
            ))}
          </div>

          {filtered.length === 0 && (
            <div data-testid="entity-picker-empty" className="text-sm text-muted-foreground border border-dashed border-border p-8 text-center">
              No entities match “{filter}”.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
